import { StrictMode } from "react";
import ReactDOM from "react-dom/client";
import { RouterProvider } from "@tanstack/react-router";
import { colorsTuple, createTheme, MantineProvider } from "@mantine/core";
import { router } from "./router";
import "@mantine/core/styles.css";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "mantine-datatable/styles.layer.css";
import "@mantine/carousel/styles.css";
import "@mantine/dates/styles.css";
import "@mdxeditor/editor/style.css";
import { ModalsProvider } from "@mantine/modals";
import { Notifications } from "@mantine/notifications";
import "@mantine/notifications/styles.css";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import "./main.css";
import "@xyflow/react/dist/style.css";
import { ReactFlowProvider } from "@xyflow/react";
import "mapbox-gl/dist/mapbox-gl.css";
import { FlareErrorBoundary } from "./components/FlareErrorBoundary";
import { maskCapturedNetworkRequestForTraceId } from "./utils/posthogCorrelation";

if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    defaults: "2025-05-24",
    enable_recording_console_log: true,
    capture_exceptions: {
      capture_console_errors: true,
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
    },
    // Per-request Tempo ids live on API response header `x-trace-id` (CORS-exposed).
    // Capture headers, then mask down to that one value for session replay.
    session_recording: {
      recordHeaders: true,
      maskCapturedNetworkRequestFn: maskCapturedNetworkRequestForTraceId,
    },
  });
}

function MaybePosthogProvider({ children }: { children: React.ReactNode }) {
  if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
    return <PostHogProvider client={posthog}>{children}</PostHogProvider>;
  }
  return <>{children}</>;
}

const queryClient = new QueryClient();

const theme = createTheme({
  cursorType: "pointer",
  colors: {
    brand: colorsTuple("#259ADD"),
  },
});
ReactDOM.createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MaybePosthogProvider>
      <FlareErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <MantineProvider defaultColorScheme="dark" theme={theme}>
            <Notifications />
            <ModalsProvider>
              <ReactFlowProvider>
                <RouterProvider router={router} />
              </ReactFlowProvider>
            </ModalsProvider>
          </MantineProvider>
          {import.meta.env.MODE === "development" && <ReactQueryDevtools />}
        </QueryClientProvider>
      </FlareErrorBoundary>
    </MaybePosthogProvider>
  </StrictMode>,
);
